import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import {
  NotificationDto,
  TaskDto,
  formatTaskCommentNotificationMessage,
  formatTaskUpdatedNotificationMessage,
  formatWorkflowTaskBlockedNotificationMessage,
  formatWorkflowTaskReopenedNotificationMessage,
  formatWorkflowTaskUnlockedNotificationMessage,
} from '@prometeus/contracts';
import { TaskDetailModal } from '../components/TaskDetailModal';
import { Breadcrumb } from '../components/Breadcrumb';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { useWorkspaceStore } from '../stores/workspace.store';
import { MencionouIcon } from '../components/icons/MencionouIcon';
import { TaskIconNotification } from '../components/icons/TaskIconNotification';
import api from '../lib/api';

export const Route = createFileRoute('/w/$workspaceId/notifications')({
  component: NotificationsPage,
});

const formatRelativeTime = (date: Date | string): string => {
  const now = new Date();
  const notificationDate = new Date(date);
  const diffSeconds = Math.floor((now.getTime() - notificationDate.getTime()) / 1000);

  if (diffSeconds < 60) return 'agora mesmo';
  if (diffSeconds < 3600) return `há ${Math.floor(diffSeconds / 60)} minutos`;
  if (diffSeconds < 86400) return `há ${Math.floor(diffSeconds / 3600)} horas`;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (notificationDate.toDateString() === yesterday.toDateString()) return 'ontem';

  return notificationDate.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getNotificationMessage = (notification: NotificationDto): string => {
  switch (notification.type) {
    case 'TASK_MENTION':
      return `${notification.data?.authorName || 'Alguém'} mencionou você em "${notification.data?.taskTitle || 'uma task'}"`;
    case 'TASK_ASSIGNED': {
      const assignedByName = notification.data?.assignedByName || 'Alguém';
      const taskTitle = notification.data?.taskTitle || 'uma task';
      const processNumber = notification.data?.processNumber;
      const processText = processNumber ? ` no processo ${processNumber}` : '';
      return `${assignedByName} designou uma tarefa para você "${taskTitle}"${processText}`;
    }
    case 'TASK_UPDATED':
      return formatTaskUpdatedNotificationMessage({
        updatedByName: notification.data?.updatedByName,
        taskTitle: notification.data?.taskTitle,
        changeLabels: notification.data?.changeLabels,
        changeSummaries: notification.data?.changeSummaries,
      });
    case 'TASK_COMMENT':
      return formatTaskCommentNotificationMessage({
        authorName: notification.data?.authorName,
        taskTitle: notification.data?.taskTitle,
      });
    case 'WORKFLOW_TASK_UNLOCKED':
      return formatWorkflowTaskUnlockedNotificationMessage({
        taskTitle: notification.data?.taskTitle,
      });
    case 'WORKFLOW_TASK_BLOCKED':
      return formatWorkflowTaskBlockedNotificationMessage({
        taskTitle: notification.data?.taskTitle,
      });
    case 'WORKFLOW_TASK_REOPENED':
      return formatWorkflowTaskReopenedNotificationMessage({
        taskTitle: notification.data?.taskTitle,
      });
    case 'CNJ_SEARCH_COMPLETED':
      return `Busca CNJ finalizada: ${notification.data?.summary || 'processamento concluído'}`;
    case 'CNJ_SEARCH_FAILED':
      return `Busca CNJ falhou: ${notification.data?.summary || 'verifique os detalhes da pesquisa'}`;
    default:
      return 'Nova notificação';
  }
};

const getNotificationIcon = (type: NotificationDto['type']) => {
  switch (type) {
    case 'TASK_MENTION':
      return (
        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'rgba(72, 90, 106, 0.1)' }}
        >
          <div style={{ color: '#485A6A' }}>
            <MencionouIcon className="w-5 h-5" />
          </div>
        </div>
      );
    case 'TASK_ASSIGNED':
    case 'WORKFLOW_TASK_UNLOCKED':
    case 'WORKFLOW_TASK_BLOCKED':
    case 'WORKFLOW_TASK_REOPENED':
      return (
        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'rgba(72, 90, 106, 0.1)' }}
        >
          <div style={{ color: '#485A6A' }}>
            <TaskIconNotification className="w-5 h-5" />
          </div>
        </div>
      );
    case 'TASK_UPDATED':
      return (
        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'rgba(234, 179, 8, 0.1)' }}
        >
          <svg 
            className="w-5 h-5" 
            style={{ color: '#EAB308' }}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </div>
      );
    case 'TASK_COMMENT':
      return (
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'rgba(59, 130, 246, 0.12)' }}
        >
          <svg
            className="w-5 h-5"
            style={{ color: '#2563EB' }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </div>
      );
    case 'CNJ_SEARCH_COMPLETED':
      return (
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'rgba(16, 185, 129, 0.12)' }}
        >
          <svg
            className="w-5 h-5"
            style={{ color: '#10B981' }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    case 'CNJ_SEARCH_FAILED':
      return (
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'rgba(239, 68, 68, 0.12)' }}
        >
          <svg
            className="w-5 h-5"
            style={{ color: '#EF4444' }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    default:
      return (
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'rgba(72, 90, 106, 0.1)' }}
        >
          <div style={{ color: '#485A6A' }}>
            <TaskIconNotification className="w-5 h-5" />
          </div>
        </div>
      );
  }
};

function NotificationsPage() {
  const { workspaceId } = Route.useParams();
  const workspace = useWorkspaceStore((state) => state.currentWorkspace);
  const { requirePermission } = usePermissionGuard();
  const [selectedTask, setSelectedTask] = useState<TaskDto | null>(null);
  const { notifications, readNotifications, isLoading, markAsRead, markTaskAsRead, refetch } = useNotifications(50, 0);
  
  // Calculate actual unread count from filtered notifications
  const actualUnreadCount = notifications.filter(n => !n.readAt).length;

  const handleNotificationClick = async (notification: NotificationDto) => {
    if (!notification.taskId) return;
    if (!requirePermission('tasks.read', 'Ver tarefas')) return;

    // Only mark as read if not already read
    if (!notification.readAt) {
      markAsRead(notification.id);
    }

    // Fetch task details
    try {
      const response = await api.get<{ data: TaskDto }>(`/tasks/${notification.taskId}`);
      setSelectedTask(response.data.data);
    } catch (error) {
      console.error('Error fetching task:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    // Mark all visible unread notifications as read
    const unreadNotifications = notifications.filter(n => !n.readAt);
    // Mark all as read sequentially to avoid race conditions
    for (const notification of unreadNotifications) {
      markAsRead(notification.id);
    }
    // Refetch after marking all to ensure consistency
    setTimeout(() => {
      refetch();
    }, 1000);
  };

  const handleCloseTaskModal = () => {
    setSelectedTask(null);
    // Refetch notifications after closing modal
    refetch();
  };

  return (
    <div className="max-w-7xl mx-auto px-8 py-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: workspace?.name || 'Workspace', to: '/workspaces' },
          { label: 'Notificações' },
        ]}
      />

      {/* Container */}
      <div
        className="bg-white dark:bg-gray-800 p-6"
        style={{
          borderRadius: '15px',
          boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
        }}
      >
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '18px',
                fontWeight: 600,
                color: '#2D3748',
                marginBottom: '4px',
              }}
              className="dark:text-gray-100"
            >
              Notificações
            </h1>
            {actualUnreadCount > 0 && (
              <p 
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#718096',
                }}
                className="dark:text-gray-400"
              >
                {actualUnreadCount} não lida{actualUnreadCount > 1 ? 's' : ''}
              </p>
            )}
          </div>
          {actualUnreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                fontWeight: 500,
                color: '#485A6A',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
              className="px-4 py-2 transition-colors hover:opacity-80"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(72, 90, 106, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Marcar todas como lidas
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div 
              className="inline-block animate-spin rounded-full h-8 w-8 border-b-2"
              style={{ borderColor: '#485A6A' }}
            ></div>
            <p 
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                fontWeight: 400,
                color: '#718096',
                marginTop: '16px',
              }}
              className="dark:text-gray-400"
            >
              Carregando notificações...
            </p>
          </div>
        ) : (
          <>
            {/* Notificações não lidas */}
            {notifications.filter(n => !n.readAt).length > 0 && (
              <div className="mb-6">
                <div
                  className="bg-white dark:bg-gray-800 divide-y"
                  style={{
                    borderRadius: '15px',
                    boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                    border: '1px solid #E2E8F0',
                    overflow: 'hidden',
                  }}
                >
                  {notifications.filter(n => !n.readAt).map((notification) => (
                    <button
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className="w-full text-left p-4 transition-colors"
                      style={{
                        backgroundColor: 'rgba(72, 90, 106, 0.05)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(72, 90, 106, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(72, 90, 106, 0.05)';
                      }}
                    >
                      <div className="flex items-start gap-4">
                        {getNotificationIcon(notification.type)}
                        <div className="flex-1 min-w-0">
                          <p 
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              fontWeight: 500,
                              color: '#2D3748',
                            }}
                            className="dark:text-gray-100"
                          >
                            {getNotificationMessage(notification)}
                          </p>
                          <p 
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '11px',
                              fontWeight: 400,
                              color: '#718096',
                              marginTop: '4px',
                            }}
                            className="dark:text-gray-400"
                          >
                            {formatRelativeTime(notification.createdAt)}
                          </p>
                        </div>
                        <div 
                          className="flex-shrink-0 w-2 h-2 rounded-full mt-2"
                          style={{ backgroundColor: '#485A6A' }}
                        />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Notificações lidas */}
            {readNotifications.filter(n => n.readAt).length > 0 && (
              <div>
                <h2 
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#2D3748',
                    marginBottom: '12px',
                  }}
                  className="dark:text-gray-100"
                >
                  Notificações lidas
                </h2>
                <div
                  className="bg-white dark:bg-gray-800 divide-y"
                  style={{
                    borderRadius: '15px',
                    boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                    border: '1px solid #E2E8F0',
                    overflow: 'hidden',
                  }}
                >
                  {readNotifications.filter(n => n.readAt).map((notification) => (
                    <button
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className="w-full text-left p-4 transition-colors"
                      style={{ backgroundColor: 'transparent' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#F9FAFB';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div className="flex items-start gap-4">
                        <div style={{ opacity: 0.5 }}>
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p 
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '12px',
                              fontWeight: 400,
                              color: '#718096',
                            }}
                            className="dark:text-gray-400"
                          >
                            {getNotificationMessage(notification)}
                          </p>
                          <p 
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '11px',
                              fontWeight: 400,
                              color: '#A0AEC0',
                              marginTop: '4px',
                            }}
                            className="dark:text-gray-500"
                          >
                            {formatRelativeTime(notification.createdAt)}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {notifications.filter(n => !n.readAt).length === 0 && readNotifications.length === 0 && (
              <div 
                className="text-center py-12"
                style={{
                  borderRadius: '15px',
                  backgroundColor: '#F9FAFB',
                }}
              >
                <svg
                  className="mx-auto h-12 w-12"
                  style={{ color: '#A0AEC0' }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                <h3 
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#2D3748',
                    marginTop: '8px',
                  }}
                  className="dark:text-gray-100"
                >
                  Nenhuma notificação
                </h3>
                <p 
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    fontWeight: 400,
                    color: '#718096',
                    marginTop: '4px',
                  }}
                  className="dark:text-gray-400"
                >
                  Você está em dia! Não há notificações no momento.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={handleCloseTaskModal}
          onSuccess={() => {
            markTaskAsRead(selectedTask.id);
            refetch();
          }}
        />
      )}
    </div>
  );
}
